import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { Star, Zap, ShoppingCart } from 'lucide-react';
import { getReadyMadeDesigns, readyMadeDisplayUrl, readyMadeToFlowSelectedImage } from '../apiClient';
import HeroSlider from './HeroSlider';
import InspirationScroll from './InspirationScroll';

const NewLandingPage = () => {
  const navigate = useNavigate();
  const [readyMade, setReadyMade] = useState([]);
  const [readyMadeLoaded, setReadyMadeLoaded] = useState(false);

  useEffect(() => {
    getReadyMadeDesigns({ limit: 48, offset: 0 })
      .then((list) => {
        setReadyMade(
          (Array.isArray(list) ? list : []).map((d) => ({
            id: `api-${d.id}`,
            dbId: d.id,
            name: d.name,
            imageUrl: readyMadeDisplayUrl(d),
            artUrl: d.image_url,
            mockupUrl: d.mockup_image_url || null,
            style: d.style,
            category: d.category,
            price: d.price || '$29.99',
          })),
        );
      })
      .finally(() => setReadyMadeLoaded(true));
  }, []);

  const featuredDesigns = readyMade.slice(0, 4);
  const heroSlides = useMemo(
    () =>
      readyMade
        .map((design) => {
          const mockup = design.mockupUrl;
          const art = design.artUrl || design.imageUrl;
          if (mockup) {
            return {
              key: design.id,
              src: mockup,
              fallbackSrc: art && art !== mockup ? art : null,
              framed: false,
            };
          }
          if (art) {
            return {
              key: design.id,
              src: art,
              fallbackSrc: null,
              framed: true,
            };
          }
          return null;
        })
        .filter(Boolean),
    [readyMade],
  );

  const handleBuyNow = (design) => {
    const flow = readyMadeToFlowSelectedImage({
      id: design.dbId,
      name: design.name,
      image_url: design.artUrl,
      style: design.style,
      mockup_image_url: design.mockupUrl ?? null,
    });
    navigate('/products', {
      state: { selectedImage: flow },
    });
  };

  return (
    <div className="bg-white text-gray-800">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-neutral-950">
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-blue-950/50 via-neutral-950 to-neutral-900" />
        <div className="relative mx-auto grid min-h-[70vh] max-w-7xl items-center gap-8 px-4 py-12 sm:px-6 sm:py-16 lg:min-h-[78vh] lg:grid-cols-2 lg:gap-12 lg:py-20">
          <div className="order-2 text-center lg:order-1 lg:text-left">
            <h1 className="mb-4 text-4xl font-extrabold text-white sm:text-5xl md:text-6xl lg:text-7xl">
              Wear Your Imagination
            </h1>
            <p className="mb-6 text-base text-gray-300 sm:mb-8 sm:text-xl md:text-2xl">
              AI-Powered Custom T-Shirts, Designed by You.
            </p>
            <Link
              to="/design"
              className="inline-block rounded-full bg-blue-600 px-8 py-3 text-base font-bold text-white shadow-lg transition duration-300 ease-in-out hover:scale-105 hover:bg-blue-700 sm:px-10 sm:py-4 sm:text-lg"
            >
              Create Your Own T-Shirt
            </Link>
          </div>
          <div className="order-1 h-[48vh] w-full sm:h-[56vh] lg:order-2 lg:h-[70vh]">
            {heroSlides.length > 0 ? (
              <HeroSlider slides={heroSlides} />
            ) : (
              <div className="flex h-full w-full items-center justify-center rounded-lg bg-neutral-900">
                <p className="text-sm text-gray-400">Loading design previews…</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Featured Designs Section */}
      <section className="py-10 sm:py-16 px-4">
        <div className="container mx-auto max-w-7xl">
          <h2 className="text-2xl sm:text-4xl font-bold text-center text-gray-800 mb-8 sm:mb-12">
            Shop Our Designs
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8">
            {!readyMadeLoaded && (
              <p className="col-span-full text-center text-gray-500 py-8">Loading designs…</p>
            )}
            {readyMadeLoaded && featuredDesigns.length === 0 && (
              <p className="col-span-full text-center text-gray-500 py-8">No Ready-Made designs yet.</p>
            )}
            {featuredDesigns.map((design) => (
              <div key={design.id} className="bg-white rounded-lg shadow-lg overflow-hidden transform hover:-translate-y-2 transition-transform duration-300">
                <img src={design.imageUrl} alt={design.name} className="w-full h-64 object-cover" />
                <div className="p-6 text-center">
                  <h3 className="text-xl font-semibold mb-2">{design.name}</h3>
                  <p className="text-lg text-gray-600 mb-4">{design.price}</p>
                  <button
                    type="button"
                    onClick={() => handleBuyNow(design)}
                    className="w-full bg-gray-800 hover:bg-gray-900 text-white font-bold py-2 px-4 rounded-full transition-colors">
                    Buy Now
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="bg-gray-50 py-12 sm:py-20 px-4">
        <div className="container mx-auto text-center max-w-7xl">
          <h2 className="text-2xl sm:text-4xl font-bold text-gray-800 mb-8 sm:mb-12">How It Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="flex flex-col items-center">
              <div className="bg-blue-600 text-white rounded-full p-4 sm:p-6 mb-4">
                <Zap size={40} />
              </div>
              <h3 className="text-2xl font-semibold mb-2">1. Imagine & Generate</h3>
              <p className="text-gray-600">Use our AI to bring your wildest t-shirt ideas to life from a simple text prompt.</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="bg-blue-600 text-white rounded-full p-6 mb-4">
                <ShoppingCart size={40} />
              </div>
              <h3 className="text-2xl font-semibold mb-2">2. Customize Your Fit</h3>
              <p className="text-gray-600">Choose your t-shirt style, color, and fit. Place your design exactly where you want it.</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="bg-blue-600 text-white rounded-full p-6 mb-4">
                <Star size={40} />
              </div>
              <h3 className="text-2xl font-semibold mb-2">3. Pay with email</h3>
              <p className="text-gray-600">No sign-in. We ship to your address and email you the confirmation.</p>
            </div>
          </div>
        </div>
      </section>

      {/* AI Showcase Section */}
      <InspirationScroll items={readyMade} onDesignSelect={(design) => handleBuyNow(design)} />

      {/* Why buy here */}
      <section className="bg-gray-50 py-12 sm:py-20 px-4">
        <div className="container mx-auto text-center max-w-7xl">
          <h2 className="text-2xl sm:text-4xl font-bold text-gray-800 mb-8 sm:mb-12">Simple to buy</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <p className="text-lg font-semibold mb-3">No account needed</p>
              <p className="text-gray-600">Pick a design, enter a shipping address and email, and pay. We use your email to confirm the order and stay in touch.</p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <p className="text-lg font-semibold mb-3">Printed on demand</p>
              <p className="text-gray-600">Each item is made after you order and shipped by Printful or Printify to a real address — so the address has to be valid.</p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <p className="text-lg font-semibold mb-3">Clear policies</p>
              <p className="text-gray-600">Shipping, refunds, and terms are on the site before you pay. Questions? Use Help or the chat widget.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-12 px-4">
        <div className="container mx-auto text-center">
          <h3 className="text-2xl font-bold mb-4">YourShirt</h3>
          <p className="text-gray-400 mb-6">Create, wear, and share your imagination.</p>
          <div className="text-gray-500">
            <p>&copy; {new Date().getFullYear()} YourShirt. All Rights Reserved.</p>
            <div className="mt-4 flex flex-wrap justify-center gap-x-4 gap-y-1">
              <Link to="/help" className="text-gray-500 hover:text-white">Help</Link>
              <Link to="/help" className="text-gray-500 hover:text-white">About</Link>
              <Link to="/terms" className="text-gray-500 hover:text-white">Terms</Link>
              <Link to="/refund-policy" className="text-gray-500 hover:text-white">Refunds</Link>
              <Link to="/privacy" className="text-gray-500 hover:text-white">Privacy</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default NewLandingPage;
